import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Alert, Select, Tabs, Tooltip } from 'antd';
import Chart from 'react-google-charts';
import { ScaleLoader } from 'react-spinners';
import { 
	INitrogenInsightsTabContentProps, 
	INitrogenMetric, 
	NitrogenPercentileRange, 
	NitrogenPrediction 
} from './Types';
import * as Styles from '../Styles/WeatherDataModalStyles';
import { convertNullableMillimetersToInches } from '../../../../Utility/UnitConversion';

// prediction dates are bare 'yyyy-MM-dd' strings; parsing them as zoned/UTC and converting to local can roll the date back a day
const parseNitrogenDate = (date: string) => moment(date, 'YYYY-MM-DD');

const nitrogenMetrics: INitrogenMetric[] = [
	{ 
		key: 'uptake', 
		label: 'Cumulative N Uptake', 
		color: Styles.CHART_COLORS.blue, 
		getValues: prediction => prediction.cumulative_n_uptake?.compare_daily_median?.values 
	},
	{ 
		key: 'losses', 
		label: 'Cumulative N Losses', 
		color: Styles.CHART_COLORS.red, 
		getValues: prediction => prediction.cumulative_n_losses?.compare_daily_median?.values 
	},
	{ 
		key: 'mineralization', 
		label: 'Cumulative N Mineralization', 
		color: Styles.CHART_COLORS.green, 
		getValues: prediction => prediction.cumulative_n_mineralization?.compare_daily_median?.values
	}
];

const getNitrogenTooltip = (date: string, label: string, value: number | null | undefined, interpretation?: string) =>
{
	if (value == null) return null;

	return [
		`<div style="${Styles.nitrogenTooltipStyle}">`,
		`<strong>${date}</strong><br>`,
		`${label}: <strong>${value}</strong>`,
		...(interpretation ? [`<br>${interpretation}`] : []),
		'</div>'
	].join('');
};

const getNitrogenRatioTooltip = (
	date: string,
	label: string,
	value: number | null | undefined,
	subject: string,
	comparisonValue = 1,
	comparisonLabel = 'historical median'
) =>
{
	if (value == null) return null;

	const comparison = value > comparisonValue ? 'above' : value < comparisonValue ? 'below' : 'equal to';
	return getNitrogenTooltip(date, label, value, `${subject} <strong>${comparison}</strong> ${comparisonLabel}`);
};

const getNitrogenGrowthStageAnnotation = (
	prediction: NitrogenPrediction,
	index: number,
	growthStageByDate: Record<string, string>
) =>
{
	const annotation = growthStageByDate[parseNitrogenDate(prediction.dates[index]).toDate().toDateString()];
	return annotation === 'Planting Date' ? undefined : annotation;
};

const getNitrogenPercentileBounds = (
	historical: NitrogenPrediction['cumulative_precipitation']['historical'],
	percentileRange: NitrogenPercentileRange
) => percentileRange === 'p10-p90'
	? { lower: historical.p10, upper: historical.p90, label: 'P10-P90' }
	: { lower: historical.p25, upper: historical.p75, label: 'P25-P75' };

const buildNitrogenChartData = (
	prediction: NitrogenPrediction,
	dateLabels: string[],
	metric: INitrogenMetric,
	growthStageByDate: Record<string, string>
) =>
{
	const values = metric.getValues(prediction);

	return [
		[
			{ type: 'string', label: 'Date' },
			{ type: 'number', label: metric.label },
			{ type: 'string', role: 'tooltip', p: { html: true } },
			{ type: 'number', label: 'Historical median' },
			{ type: 'string', role: 'tooltip', p: { html: true } },
			{ type: 'string', role: 'annotation' }
		],
		...dateLabels.map((dateLabel, index) => [
			dateLabel,
			values?.[index] ?? null,
			getNitrogenRatioTooltip(dateLabel, metric.label, values?.[index], `N ${metric.key}`),
			1,
			getNitrogenTooltip(dateLabel, 'Historical median', 1),
			getNitrogenGrowthStageAnnotation(prediction, index, growthStageByDate)
		])
	];
};

const buildNitrogenMaturityChartData = (
	prediction: NitrogenPrediction,
	dateLabels: string[],
	percentileRange: NitrogenPercentileRange,
	growthStageByDate: Record<string, string>
) =>
{
	const maturitySeries = prediction.cumulative_n_uptake.compare_maturity_median;
	const bounds = getNitrogenPercentileBounds(maturitySeries.historical, percentileRange);

	return [
		[
			{ type: 'string', label: 'Date' },
			{ type: 'number', label: 'Current uptake' },
			{ type: 'string', role: 'tooltip', p: { html: true } },
			{ type: 'number', label: 'Historical median (same date)' },
			{ type: 'string', role: 'tooltip', p: { html: true } },
			{ type: 'string', role: 'annotation' },
			{ type: 'number', label: `${bounds.label} lower`, role: 'interval' },
			{ type: 'number', label: `${bounds.label} upper`, role: 'interval' }
		],
		...dateLabels.map((dateLabel, index) => [
			dateLabel,
			maturitySeries.current?.[index] ?? null,
			getNitrogenRatioTooltip(
				dateLabel,
				'Current uptake',
				maturitySeries.current?.[index],
				'N uptake',
				maturitySeries.historical.p50[index],
				'same-date historical median'
			),
			maturitySeries.historical.p50[index],
			getNitrogenTooltip(dateLabel, 'Historical median (same date)', maturitySeries.historical.p50[index]),
			getNitrogenGrowthStageAnnotation(prediction, index, growthStageByDate),
			bounds.lower[index] ?? null,
			bounds.upper[index] ?? null
		])
	];
};

const buildNitrogenPrecipitationChartData = (
	prediction: NitrogenPrediction,
	dateLabels: string[],
	percentileRange: NitrogenPercentileRange,
	growthStageByDate: Record<string, string>
) =>
{
	const precipitation = prediction.cumulative_precipitation;
	const bounds = getNitrogenPercentileBounds(precipitation.historical, percentileRange);

	return [
		[
			{ type: 'string', label: 'Date' },
			{ type: 'number', label: 'Current precipitation (in)' },
			{ type: 'string', role: 'tooltip', p: { html: true } },
			{ type: 'number', label: 'Historical median (in) - p50' },
			{ type: 'string', role: 'tooltip', p: { html: true } },
			{ type: 'string', role: 'annotation' },
			{ type: 'number', label: `${bounds.label} lower`, role: 'interval' },
			{ type: 'number', label: `${bounds.label} upper`, role: 'interval' }
		],
		...dateLabels.map((dateLabel, index) =>
		{
			const current = convertNullableMillimetersToInches(precipitation.current[index]);
			const historicalMedian = convertNullableMillimetersToInches(precipitation.historical.p50[index]);
			const comparison = current != null && historicalMedian != null
				? current > historicalMedian ? 'above' : current < historicalMedian ? 'below' : 'equal to'
				: undefined;
			return [
				dateLabel,
				current,
				getNitrogenTooltip(dateLabel, 'Current precipitation (in)', current,
					comparison ? `Precipitation <strong>${comparison}</strong> historical median` : undefined),
				historicalMedian,
				getNitrogenTooltip(dateLabel, 'Historical median (in) - p50', historicalMedian),
				getNitrogenGrowthStageAnnotation(prediction, index, growthStageByDate),
				convertNullableMillimetersToInches(bounds.lower[index]),
				convertNullableMillimetersToInches(bounds.upper[index])
			];
		})
	];
};

const hasAlignedNitrogenPrediction = (prediction: NitrogenPrediction) =>
{
	const dateCount = prediction.dates.length;
	const seriesLengths = [
		prediction.cumulative_n_uptake.compare_daily_median.values.length,
		prediction.cumulative_n_uptake.compare_maturity_median.current.length,
		prediction.cumulative_n_uptake.compare_maturity_median.historical.p10.length,
		prediction.cumulative_n_uptake.compare_maturity_median.historical.p25.length,
		prediction.cumulative_n_uptake.compare_maturity_median.historical.p50.length,
		prediction.cumulative_n_uptake.compare_maturity_median.historical.p75.length,
		prediction.cumulative_n_uptake.compare_maturity_median.historical.p90.length,
		prediction.cumulative_n_losses.compare_daily_median.values.length,
		prediction.cumulative_n_mineralization.compare_daily_median.values.length,
		prediction.cumulative_precipitation.current.length,
		prediction.cumulative_precipitation.historical.p10.length,
		prediction.cumulative_precipitation.historical.p25.length,
		prediction.cumulative_precipitation.historical.p50.length,
		prediction.cumulative_precipitation.historical.p75.length,
		prediction.cumulative_precipitation.historical.p90.length
	];

	return dateCount > 0 && seriesLengths.every(length => length === dateCount);
};

const doesRelativeMaturityStartWith = (
	relativeMaturities: string[] | undefined,
	relativeMaturity: string,
	searchText: string
) : boolean =>
{
	return relativeMaturities?.includes(relativeMaturity)
		? relativeMaturity.startsWith(searchText)
		: false;
};

const plantingDateYear = moment().year();
const earliestPlantingDate = moment([plantingDateYear, 2, 1]);
const latestPlantingDate = moment([plantingDateYear, 6, 15]);
const plantingDateRangeTooltip = `Available dates are ${earliestPlantingDate.format('MM/DD/YYYY')} through ${latestPlantingDate.format('MM/DD/YYYY')}. If the request fails or returns no insights, select an earlier planting date and try again.`;
const getLatestAvailablePlantingDate = () => moment.min(moment.max(moment(), earliestPlantingDate), latestPlantingDate);

export const NitrogenInsightsTabContent = (props: INitrogenInsightsTabContentProps) =>
{
	const { data, error, growthStageByDate, onNitrogenInputsSelected } = props;
	const [percentileRange, setPercentileRange] = useState<NitrogenPercentileRange>('p25-p75');
	const [selectedRelativeMaturity, setSelectedRelativeMaturity] = useState<string>();
	const [selectedPlantingDate, setSelectedPlantingDate] = useState<moment.Moment | null>(null);

	// Keep the dropdown/title in sync with the RM the backend actually queried (e.g. the assigned corn hybrid's own RM)
	useEffect(() =>
	{
		if (data?.queriedRelativeMaturity)
		{
			setSelectedRelativeMaturity(data.queriedRelativeMaturity);
		}
	}, [data?.queriedRelativeMaturity]);

	useEffect(() =>
	{
		if (data?.queriedPlantingDate)
		{
			setSelectedPlantingDate(moment.parseZone(data.queriedPlantingDate));
		}
	}, [data?.queriedPlantingDate]);

	// Only a single field is ever requested, so only the first result is relevant
	const prediction = data?.nitrogenInsights?.results?.[0]?.prediction;

	const dateLabels = useMemo(() => prediction?.dates?.map(date => parseNitrogenDate(date).format('MMM DD')), [prediction]);

	const chartDataByMetric = useMemo(() =>
	{
		if (!prediction || !dateLabels)
		{
			return undefined;
		}

		return nitrogenMetrics.reduce((acc, metric) =>
		{
			acc[metric.key] = buildNitrogenChartData(prediction, dateLabels, metric, growthStageByDate);
			return acc;
		}, {} as Record<string, unknown[][]>);
	}, [prediction, dateLabels, growthStageByDate]);

	const maturityChartData = useMemo(() =>
	{
		if (!prediction || !dateLabels)
		{
			return undefined;
		}

		return buildNitrogenMaturityChartData(prediction, dateLabels, percentileRange, growthStageByDate);
	}, [prediction, dateLabels, percentileRange, growthStageByDate]);

	const precipitationChartData = useMemo(() =>
	{
		if (!prediction || !dateLabels)
		{
			return undefined;
		}

		return buildNitrogenPrecipitationChartData(prediction, dateLabels, percentileRange, growthStageByDate);
	}, [prediction, dateLabels, percentileRange, growthStageByDate]);

	// No corn hybrid is currently assigned to the field
	const isMissingAssignedHybrid = data?.isAssignedSeedCorn === false && !data?.nitrogenInsights;
	const isLoadingNitrogenInsights = !error && data === undefined;

	let promptMessage: string | undefined;
	if (isLoadingNitrogenInsights)
	{
		promptMessage = 'Loading nitrogen insights. Please wait while we retrieve the latest data...';
	}
	else
	{
		promptMessage = isMissingAssignedHybrid
			? 'No corn hybrid is assigned to this field. Select a relative maturity and planting date to view nitrogen insights:'
			: 'To change the Nitrogen Insights, select a relative maturity and / or planting date:';
	}

	const hasValidChartData = !!prediction && hasAlignedNitrogenPrediction(prediction);
	const showCharts = !error && !!chartDataByMetric && hasValidChartData;

	const relativeMaturitySuffix = selectedRelativeMaturity ? ` - RM${selectedRelativeMaturity}` : '';
	const plantingDateSuffix = data?.queriedPlantingDate
		? ` - Planting Date ${moment.parseZone(data.queriedPlantingDate).format('MM/DD/YYYY')}`
		: '';
	const chartTitleSuffix = `${relativeMaturitySuffix}${plantingDateSuffix}`;

	const onSelectRelativeMaturity = (relativeMaturity: string) =>
	{
		setSelectedRelativeMaturity(relativeMaturity);
		if (selectedPlantingDate)
		{
			onNitrogenInputsSelected(relativeMaturity, selectedPlantingDate.format('YYYY-MM-DD'));
		}
	};

	const onSelectPlantingDate = (plantingDate: moment.Moment | null) =>
	{
		setSelectedPlantingDate(plantingDate);
		if (plantingDate && selectedRelativeMaturity)
		{
			onNitrogenInputsSelected(selectedRelativeMaturity, plantingDate.format('YYYY-MM-DD'));
		}
	};

	return (
		<div>
			<div style={Styles.nitrogenControlsContainerStyle}>
				{error && <p>{error}</p>}
				{!error && data?.isUsingDefaultValues && (
					<Alert
						type="info"
						showIcon
						style={{ marginBottom: 14 }}
						description={
							<div>
								These insights were generated using default values for relative maturity and planting date.
							</div>
						}
					/>
				)}
				{promptMessage && <p>{promptMessage}</p>}
				<div style={Styles.nitrogenInputsContainerStyle}>
					<Select
						aria-label="Relative Maturity"
						style={Styles.nitrogenInputStyle}
						placeholder="Select a relative maturity"
						value={selectedRelativeMaturity}
						showSearch
						optionFilterProp="children"
						disabled={isLoadingNitrogenInsights}
						filterOption={(input, option) => option?.value !== undefined &&
							doesRelativeMaturityStartWith(data?.availableCornHybridRelativeMaturities, option.value as string, input)}
						onChange={onSelectRelativeMaturity}
					>
						{data?.availableCornHybridRelativeMaturities?.map(relativeMaturity => (
							<Select.Option key={relativeMaturity} value={relativeMaturity}>
								RM{relativeMaturity}
							</Select.Option>
						))}
					</Select>
					<Tooltip
						placement="top"
						title={plantingDateRangeTooltip}
					>
						<span style={{ display: 'inline-block' }}>
							<Styles.StyledDatePicker
								key={selectedPlantingDate?.format('YYYY-MM-DD') ?? 'unselected'}
								aria-label="Planting Date"
								style={Styles.nitrogenInputStyle}
								placeholder="mm/dd/yyyy"
								format="MM/DD/YYYY"
								value={selectedPlantingDate}
								defaultPickerValue={selectedPlantingDate ?? getLatestAvailablePlantingDate()}
								disabled={isLoadingNitrogenInsights}
								disabledDate={date => date.isBefore(earliestPlantingDate, 'day') || date.isAfter(latestPlantingDate, 'day')}
								onChange={onSelectPlantingDate}
								panelRender={originalPanel => <Styles.StyledDatePickerPanel>{originalPanel}</Styles.StyledDatePickerPanel>}
							/>
						</span>
					</Tooltip>
					{showCharts && (
						<Tooltip
							placement="right"
							title={
								<div>
									<div><strong>Historical range</strong></div>
									<div>Typical range (P25-P75): the middle 50% of historical outcomes.</div>
									<div>Full range (P10-P90): the broader 80% span, including less common outcomes.</div>
									<div>This changes the shaded historical range only. Current values and the 1.0 median line do not change.</div>
								</div>
							}
						>
							<span style={Styles.historicalRangeTooltipStyle}>
								<Select
									aria-label="Historical percentile range"
									style={Styles.historicalRangeSelectStyle}
									value={percentileRange}
									onChange={setPercentileRange}
								>
									<Select.Option value="p25-p75" title="">Typical range (P25-P75)</Select.Option>
									<Select.Option value="p10-p90" title="">Full range (P10-P90)</Select.Option>
								</Select>
							</span>
						</Tooltip>
					)}
				</div>
			</div>
			{!error && !chartDataByMetric && !isMissingAssignedHybrid && (
				<div style={Styles.nitrogenLoadingStyle}>
					<ScaleLoader />
				</div>
			)}
			{!error && !!prediction && !hasValidChartData && (
				<p style={Styles.nitrogenMessageStyle}>Nitrogen insights are unavailable because the returned data is incomplete.</p>
			)}
			{showCharts && (
				<Tabs
					destroyInactiveTabPane
					items={[
						{
							label: 'N Uptake vs. Historical Range',
							key: 'uptake-maturity',
							children: (
								<Chart
									chartType="LineChart"
									options={{
										title: `Corn - N Uptake vs. Historical Range${chartTitleSuffix}`,
										legend: { position: 'bottom' },
										series: [
											{ color: Styles.CHART_COLORS.blue }, 
											{ color: Styles.CHART_COLORS.darkGray, lineWidth: 1.5 }
										],
										tooltip: { isHtml: true },
										intervals: { style: 'area', color: Styles.CHART_COLORS.lightBlue },
										interval: { i0: { style: 'area', fillOpacity: 0.25, color: Styles.CHART_COLORS.lightBlue } },
										hAxis: { title: 'Date' },
										vAxis: { 
											title: 'Cumulative N uptake (ratio relative to historical median at physiological maturity)' 
										},
										backgroundColor: Styles.CHART_COLORS.backgroundColor
									}}
									data={maturityChartData}
									width='100%'
									height='60vh'
								/>
							)
						},
						{
							label: 'Daily N Uptake vs. Historical Median',
							key: 'uptake-daily',
							children: (
								<Chart
									chartType="LineChart"
									options={{
										title: `Corn - Daily N Uptake vs. Historical Median${chartTitleSuffix}`,
										legend: { position: 'bottom' },
										series: [
											{ color: Styles.CHART_COLORS.orange }, 
											{ color: Styles.CHART_COLORS.darkGray, lineWidth: 1.5 }
										],
										tooltip: { isHtml: true },
										hAxis: { title: 'Date' },
										vAxis: { 
											title: 'Cumulative N uptake (ratio of current season to historical median)', 
											baseline: 1, 
											baselineColor: Styles.CHART_COLORS.darkGray 
										},
										backgroundColor: Styles.CHART_COLORS.backgroundColor
									}}
									data={chartDataByMetric?.uptake}
									width='100%'
									height='60vh'
								/>
							)
						},
						...nitrogenMetrics.filter(metric => metric.key !== 'uptake').map(metric => ({
							label: metric.label,
							key: metric.key,
							children: (
								<Chart
									chartType="LineChart"
									options={{
										title: `Corn - ${metric.label}${chartTitleSuffix}`,
										legend: { position: 'bottom' },
										series: [
											{ color: metric.color },
											{ color: Styles.CHART_COLORS.darkGray, lineWidth: 1.5 }
										],
										tooltip: { isHtml: true },
										hAxis: { title: 'Date' },
										vAxis: { 
											title: `Cumulative N ${metric.key === 'losses' ? 'losses' : 'mineralization'} (ratio of current season to historical median)`, 
											baseline: 1, 
											baselineColor: 
											Styles.CHART_COLORS.darkGray 
										},
										backgroundColor: Styles.CHART_COLORS.backgroundColor
									}}
									data={chartDataByMetric?.[metric.key]}
									width='100%'
									height='60vh'
								/>
							)
						})),
						{
							label: 'Cumulative Precipitation',
							key: 'precipitation',
							children: (
								<Chart
									chartType="LineChart"
									options={{
										title: `Cumulative Precipitation${chartTitleSuffix}`,
										legend: { position: 'bottom' },
										series: [
											{ color: Styles.CHART_COLORS.blue }, 
											{ color: Styles.CHART_COLORS.darkGray, lineWidth: 1.5 }
										],
										tooltip: { isHtml: true },
										intervals: { style: 'area', color: Styles.CHART_COLORS.lightBlue },
										interval: { i0: { style: 'area', fillOpacity: 0.25, color: Styles.CHART_COLORS.lightBlue } },
										hAxis: { title: 'Date' },
										vAxis: { title: 'Cumulative precipitation (in)' },
										backgroundColor: Styles.CHART_COLORS.backgroundColor
									}}
									data={precipitationChartData}
									width='100%'
									height='60vh'
								/>
							)
						}
					]}
				/>
			)}
		</div>
	);
};